import ModalViewModel from 'infra/ModalViewModel'
import { task } from 'mobx-task'
import Job from 'jobs/Job'
import { AlertFactory, AlertState } from 'alerts/Alert'
import SubmitJobDialogAdapter from './SubmitJobDialogAdapter'
import { computed, makeObservable, observable } from 'mobx'
import { Subject } from 'rxjs'

export default class SubmitDialogViewModel extends ModalViewModel {
  @observable
  shouldBillExternal = true

  /*
   * Indicates whether the job has been submitted.
   */
  private submitted = new Subject<void>()

  /*
   * Emits a value when the job is submitted.
   */
  readonly submitted$ = this.submitted.asObservable()

  constructor(
    public job: Job,
    private adapter: SubmitJobDialogAdapter,
    private alertFactory: AlertFactory,
    public canUpdateJobAsAdmin: boolean,
  ) {
    super()
    makeObservable(this)

    this.shouldBillExternal = job.billingType === 'EXTERNAL'
  }

  /**
   * Whether the billing is enabled.
   */
  @computed
  get isBillingEnabled() {
    return this.job.isBillingEnabled
  }

  /**
   * Shows the dialog.
   */
  async show() {
    if (this.showing) {
      return
    }

    this.submit.reset()

    super.show()
  }

  /**
   * Submits the job using the adapter.
   */
  submit = task.resolved(async () => {
    const alert = this.alertFactory()
      .withState(AlertState.Pending)
      .saying('Submitting job', 'Hang tight!')
      .show()
      .autoDismiss()

    try {
      await this.adapter.submitRequest(
        this.job,
        // Only people who can update jobs as admin can ever set billing to external
        this.canUpdateJobAsAdmin && this.shouldBillExternal,
      )

      // Emits the job has been submitted.
      this.submitted.next()

      this.close()

      alert
        .withState(AlertState.Success)
        .saying('Success!', 'Job has been submitted!')
        .autoDismiss()
    } catch (e) {
      alert
        .withState(AlertState.Error)
        .saying('Failed!', 'Could not submit this job.')
        .autoDismiss()

      throw e
    }
  })
}
